import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";

export const STAT_RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 days"]];

export function useMidStats(path, range) {
    const [data, setData] = useState(null);
    const [tick, setTick] = useState(0);
    useEffect(() => {
        let alive = true;
        api.get(`${path}?range=${range}`).then((r) => alive && setData(r.data)).catch(() => alive && setData({ items: [], totals: null }));
        return () => { alive = false; };
    }, [path, range, tick]);
    return [data, () => setTick((t) => t + 1)];
}

export function RangeSwitch({ value, onChange, testid = "mid-stats-range" }) {
    return (
        <div className="inline-flex bg-slate-100 rounded-lg p-0.5 gap-0.5" data-testid={testid}>
            {STAT_RANGES.map(([id, label]) => (
                <button key={id} type="button" onClick={() => onChange(id)} data-testid={`${testid}-${id}`}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${value === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{label}</button>
            ))}
        </div>
    );
}

export const rateTone = (r) => (r == null ? "text-slate-400" : r >= 70 ? "text-emerald-700" : r >= 40 ? "text-amber-700" : "text-rose-700");

/** Compact one-line stats for a MID row. */
export function MidStatsStrip({ s, testid }) {
    if (!s) return null;
    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-slate-500" data-testid={testid}>
            <span><strong className="text-slate-800 font-semibold">{s.total}</strong> orders</span>
            <span className="text-emerald-700">{s.captured} captured</span>
            <span className="text-rose-700">{s.failed} failed</span>
            <span className="text-amber-700">{s.awaiting} awaiting</span>
            <span className={`font-semibold ${rateTone(s.success_rate_pct)}`}>{s.success_rate_pct == null ? "—" : `${s.success_rate_pct}%`} success</span>
            <span className="font-mono text-slate-700">{fmtINR(s.captured_amount)}</span>
        </div>
    );
}

/** Full table of per-MID stats. */
export function MidStatsTable({ data, testid = "mid-stats-table" }) {
    const items = data?.items || [];
    const t = data?.totals;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid={testid}>
                <thead className="bg-slate-50 text-slate-600">
                    <tr>
                        <th className="px-3 py-2 text-left">MID</th>
                        <th className="px-3 py-2 text-right">Orders</th>
                        <th className="px-3 py-2 text-right">Captured</th>
                        <th className="px-3 py-2 text-right">Failed</th>
                        <th className="px-3 py-2 text-right">Awaiting</th>
                        <th className="px-3 py-2 text-right">Success</th>
                        <th className="px-3 py-2 text-right">Captured ₹</th>
                    </tr>
                </thead>
                <tbody>
                    {data === null ? (
                        <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
                    ) : items.length === 0 ? (
                        <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid={`${testid}-empty`}>No MID activity in this range.</td></tr>
                    ) : items.map((s) => (
                        <tr key={s.mid_id} className="border-t border-slate-100" data-testid={`${testid}-row-${s.mid_id}`}>
                            <td className="px-3 py-2">
                                <div className="font-semibold">{s.label}</div>
                                <div className="text-[10px] text-slate-400 font-mono">{s.mid_id}</div>
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{s.total}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-700">{s.captured}</td>
                            <td className="px-3 py-2 text-right font-mono text-rose-700">{s.failed}</td>
                            <td className="px-3 py-2 text-right font-mono text-amber-700">{s.awaiting}</td>
                            <td className={`px-3 py-2 text-right font-mono font-semibold ${rateTone(s.success_rate_pct)}`}>{s.success_rate_pct == null ? "—" : `${s.success_rate_pct}%`}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtINR(s.captured_amount)}</td>
                        </tr>
                    ))}
                </tbody>
                {t && items.length > 0 && (
                    <tfoot className="border-t-2 border-slate-200 bg-slate-50/60 font-semibold" data-testid={`${testid}-totals`}>
                        <tr>
                            <td className="px-3 py-2">Total</td>
                            <td className="px-3 py-2 text-right font-mono">{t.total}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-700">{t.captured}</td>
                            <td className="px-3 py-2 text-right font-mono text-rose-700">{t.failed}</td>
                            <td className="px-3 py-2 text-right font-mono text-amber-700">{t.awaiting}</td>
                            <td className={`px-3 py-2 text-right font-mono ${rateTone(t.success_rate_pct)}`}>{t.success_rate_pct == null ? "—" : `${t.success_rate_pct}%`}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtINR(t.captured_amount)}</td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

/** Card wrapper: title + range switch + table. Used for admin tab and partner dashboard. */
export function MidStatsCard({ path, title = "MID usage", testid = "mid-stats-card" }) {
    const [range, setRange] = useState("today");
    const [data] = useMidStats(path, range);
    return (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
            <div className="p-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                    <div className="text-sm font-semibold">{title}</div>
                    <div className="text-[11px] text-slate-500">{data?.range_label ? `${data.range_label} · IST` : "IST"}</div>
                </div>
                <RangeSwitch value={range} onChange={setRange} testid={`${testid}-range`} />
            </div>
            <MidStatsTable data={data} testid={`${testid}-table`} />
        </div>
    );
}
